import { Action, createReducer, on } from '@ngrx/store';

import {
  AreaOfInterest,
  BusinessCard,
  jobInformationFields as businessCardFields,
  requiredFields as businessCardMandatoryFields,
  EmployeeCount,
  JobInformation,
} from '@planone/bau365/util-user-data';

import * as DataAccessUserActions from './data-access-user.actions';
import { sendBusinessCardButtonActions } from './data-access-user.actions';
import { Profession, SpecializationSynonyms, Synonym } from './data-access-user.models';

export const DATA_ACCESS_USER_FEATURE_KEY = 'dataAccessUser';

type BusinessCardField = { name: string; label: string };

export interface State {
  professions: Profession[];
  synonyms: SpecializationSynonyms[];

  specialization?: string;
  profession?: string;
  synonym?: Synonym;

  userProfileTitle: string;
  userProfileShortDescription: string;
  profileLoadingFailed?: boolean;
  userJobInformation?: JobInformation;
  employeeCountOptions?: EmployeeCount[];
  areaOfInterestOptions?: AreaOfInterest[];
  userRoles?: string[];
  deletionFormSubmitted: boolean;
  businessCardLoaded?: boolean;
  businessCard?: BusinessCard;
  businessCardFields: BusinessCardField[];
  businessCardMandatoryFields: BusinessCardField[];
  isProfileImageUploaded?: boolean;
  profileImageUploadError?: string;
  sendBusinessCardSuccess: string[];
}

export interface DataAccessUserPartialState {
  readonly [DATA_ACCESS_USER_FEATURE_KEY]: State;
}

export const initialState: State = {
  professions: [],
  synonyms: [],
  userProfileTitle: '',
  userProfileShortDescription: '',
  deletionFormSubmitted: false,
  businessCardFields,
  businessCardMandatoryFields,
  isProfileImageUploaded: true,
  sendBusinessCardSuccess: [],
};

const dataAccessUserReducer = createReducer(
  initialState,
  on(DataAccessUserActions.professionsPartialLoadSucceeded, (state, { professions }) => ({
    ...state,
    professions,
  })),
  on(DataAccessUserActions.synonymsLoadSucceeded, (state, { synonyms }) => ({
    ...state,
    synonyms,
  })),
  on(DataAccessUserActions.professionSpecializationSubmitted, (state, { profession, specialization, synonym }) => ({
    ...state,
    profession,
    specialization,
    synonym,
  })),
  on(
    DataAccessUserActions.userProfessionalInformationLoadSucceeded,
    (state, { professions, specializations, synonyms }) => ({
      ...state,
      profession: professions ? professions[0] : undefined,
      specialization: specializations ? specializations[0] : undefined,
      synonym: synonyms && synonyms.length > 0 ? synonyms[0] : undefined,
    })
  ),
  on(DataAccessUserActions.userInterestsLoadSucceeded, (state, { productGroups, topics }) => ({
    ...state,
    selectedProductGroups: productGroups,
    selectedTrendTopics: topics,
  })),
  on(DataAccessUserActions.userProfileLoadedSucceeded, (state, { title, shortDescription }) => ({
    ...state,
    userProfileTitle: title,
    userProfileShortDescription: shortDescription,
  })),
  on(DataAccessUserActions.userProfileSubmitSucceeded, (state, { title, shortDescription }) => ({
    ...state,
    userProfileTitle: title,
    userProfileShortDescription: shortDescription,
  })),
  on(
    DataAccessUserActions.savedProfessionSpecializationLoaded,
    DataAccessUserActions.professionSpecializationsSubmitSucceeded,
    (state) => ({
      ...state,
      profileLoadingFailed: false,
    })
  ),
  on(DataAccessUserActions.savedProfessionSpecializationNotFound, (state) => ({
    ...state,
    profileLoadingFailed: true,
  })),
  on(DataAccessUserActions.userJobInformationSubmitSucceeded, (state, { jobInformation }) => ({
    ...state,
    userJobInformation: jobInformation,
  })),
  on(DataAccessUserActions.userJobInformationLoadSucceeded, (state, { jobInformation }) => ({
    ...state,
    userJobInformation: jobInformation,
  })),
  on(DataAccessUserActions.loadEmployeeCountOptionsSuccess, (state, { employeeCountOptions }) => ({
    ...state,
    employeeCountOptions: employeeCountOptions,
  })),
  on(DataAccessUserActions.loadAreaOfInterestOptionsSuccess, (state, { areaOfInterestOptions }) => ({
    ...state,
    areaOfInterestOptions: areaOfInterestOptions,
  })),
  on(DataAccessUserActions.userProfileCompletionPageOpened, DataAccessUserActions.userProfilePageOpened, (state) => ({
    ...state,
    topics: [],
    productGroups: [],
  })),
  on(DataAccessUserActions.userProfilePageOpened, (state) => ({
    ...state,
    selectedProfessionTopics: [],
    selectedProductGroups: [],
  })),
  on(DataAccessUserActions.userRolesLoaded, (state, { userRoles }) => ({
    ...state,
    userRoles: userRoles,
  })),
  on(DataAccessUserActions.deleteAccountSubmitSucceeded, (state) => ({
    ...state,
    deletionFormSubmitted: true,
  })),
  on(
    DataAccessUserActions.loadBusinessCardSuccess,
    DataAccessUserActions.createBusinessCardSuccess,
    DataAccessUserActions.resetBusinessCardSuccess,
    (state, { businessCard }) => ({
      ...state,
      businessCard,
      businessCardLoaded: true,
    })
  ),
  on(DataAccessUserActions.userProfileImageSubmitted, (state) => ({
    ...state,
    isProfileImageUploaded: false,
    profileImageUploadError: undefined,
  })),
  on(DataAccessUserActions.userProfileImageSubmitSucceeded, (state) => ({
    ...state,
    isProfileImageUploaded: true,
  })),
  on(DataAccessUserActions.userProfileImageSubmitFailed, (state, { error }) => ({
    ...state,
    isProfileImageUploaded: true,
    profileImageUploadError: error,
  })),
  on(sendBusinessCardButtonActions.sendBusinessCardSuccessful, (state, { exhibitorId }) => ({
    ...state,
    sendBusinessCardSuccess: [...state.sendBusinessCardSuccess, exhibitorId],
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return dataAccessUserReducer(state, action);
}
